<template>
  <ComboboxRoot
    :modelValue="modelValue"
    :open="open"
    v-model:searchTerm="searchTerm"
    ref="filterRef"
    class="relative"
    @click.stop
  >
    <ComboboxAnchor>
      <Trigger :label="triggerLabel" @click="emit('update:open', !open)">
        <template #trigger>
          <Selection
            v-if="selectionText.length && showSelection"
            :name="name"
            :text="selectionText"
            @remove="handleRemoveFilter"
          />
        </template>
      </Trigger>
    </ComboboxAnchor>
    <ComboboxContent
      class="absolute z-10 w-[500px] max-h-[400px] mt-2 bg-primary overflow-hidden rounded-md border border-secondary shadow-lg"
    >
      <Search
        v-if="showSearch"
        :placeholder="inputPlaceholder"
        :cancelLabel="cancelLabel"
        :showCancel="!isEmpty(searchTerm)"
      />
      <ComboboxViewport
        :class="{
          'pt-2': !showSearch,
          'pb-2': !showClear && type == 'space',
        }"
      >
        <div class="px-1.5 flex-grow overflow-y-auto w-full">
          <div class="flex flex-col gap-1 pb-1 w-full">
            <TreeNode
              v-if="type === 'asset_group'"
              :key="'no_asset_group'"
              :node="{
                name: t('components.filters.spaces_or_assets.no_asset_group'),
                id: 'no_asset_group',
                identifier: null,
                type: type,
                children: [],
              }"
              :selectedNodeIds="modelValue"
              @update:filters="handleUpdateFilters"
              :type="type"
            />
            <TreeNode
              v-for="node in tree"
              :key="node.id"
              :node="node"
              :level="1"
              :breadcrumb="node.name"
              :selectedNodeIds="modelValue"
              :shouldAutoOpen="node.shouldAutoOpen"
              :searchTerm="searchTerm"
              @update:filters="handleUpdateFilters"
              :type="type"
            />
          </div>
        </div>
      </ComboboxViewport>
      <div v-if="showClear">
        <ComboboxSeparator class="border-secondary border-t" />
        <div class="flex justify-between items-center px-3 py-2">
          <Clear
            v-if="showClear"
            @clear="handleClear"
            :label="clearLabel"
            :disabled="isEmpty(selectedOptions(modelValue))"
          />
          <Checkbox
            v-if="type === 'space' && showIncludeAssets"
            size="sm"
            :checked="includeAssets"
            :label="t('components.filters.spaces_or_assets.include_assets')"
            @click="handleUpdateIncludeAssets(includeAssets)"
          />
        </div>
      </div>
    </ComboboxContent>
  </ComboboxRoot>
</template>

<script setup>
// Vue core imports
import { ref, watchEffect } from "vue";

// Third-party library imports
import { isEmpty } from "lodash";
import { useI18n } from "vue-i18n";

// Local component imports
import TreeNode from "../Tree/TreeNode.vue";
import { Search, Clear, Trigger, Selection } from "@/SharedComponents/Combobox";
import { Checkbox } from "@/SharedComponents/Checkboxes";

// Radix Vue component imports
import {
  ComboboxAnchor,
  ComboboxContent,
  ComboboxSeparator,
  ComboboxRoot,
  ComboboxViewport,
} from "radix-vue";

// Custom composables
import { useClickOutside } from "@/Composables/useClickOutside";
import { useSpacesAndAssetsTree } from "@/Composables/useSpacesAndAssetsTree";

/**
 * Define emitted events for the component
 */
const emit = defineEmits([
  "update:modelValue",
  "update:assets",
  "update:includeAssets",
  "removeFilter",
  "update:open",
]);

/**
 * Define props for the component
 *
 * @property {Array} buildings - An array of building objects to be displayed in the tree
 * @property {string} type - The type of tree to display ('space' or 'asset')
 * @property {string} [name] - Optional name for the filter
 * @property {boolean} [showClear=false] - Whether to show the clear button
 * @property {boolean} [showSearch=false] - Whether to show the search input
 * @property {boolean} [showSelection=true] - Whether to show the selected items
 * @property {Array} [modelValue=[]] - The currently selected items
 * @property {string} triggerLabel - The label for the trigger button
 * @property {string} [inputPlaceholder] - Placeholder text for the search input
 * @property {string} [cancelLabel] - Label for the cancel button
 * @property {string} [clearLabel] - Label for the clear button
 * @property {boolean} [includeAssets=false] - Whether to include assets in the tree (only applicable for 'space' type)
 * @property {boolean} [open=false] - Whether the combobox is open
 */
const props = defineProps({
  buildings: {
    type: Array,
    required: true,
  },
  type: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: false,
  },
  showClear: {
    type: Boolean,
    default: false,
  },
  showSearch: {
    type: Boolean,
    default: false,
  },
  showSelection: {
    type: Boolean,
    default: true,
  },
  modelValue: {
    type: Array,
    default() {
      return [];
    },
  },
  triggerLabel: {
    type: String,
    required: true,
  },
  inputPlaceholder: {
    type: String,
    required: false,
  },
  cancelLabel: {
    type: String,
    required: false,
  },
  clearLabel: {
    type: String,
    required: false,
  },
  includeAssets: {
    type: Boolean,
    default: false,
  },
  open: {
    type: Boolean,
    default: false,
  },
  showIncludeAssets: {
    type: Boolean,
    default: false,
    required: false,
  },
});

/** Reference to the filter element */
const filterRef = ref(null);

/** Internationalization function */
const { t } = useI18n();

/**
 * Handles the removal of the filter
 * Clears the selection and emits the removeFilter event
 */
const handleRemoveFilter = () => {
  handleClear();
  emit("removeFilter");
};

/**
 * Closes the combobox when clicking outside of it
 */
useClickOutside(filterRef, () => {
  emit("update:open", false);
});

/**
 * Use the spaces and assets tree composable to manage the tree state and interactions
 * @typedef {Object} SpacesAndAssetsTree
 * @property {Function} handleClear - Function to clear the selected items
 * @property {Function} handleUpdateFilters - Function to update the selected filters
 * @property {Function} handleUpdateIncludeAssets - Function to update the includeAssets flag
 * @property {Ref<string>} searchTerm - Reactive reference for the search term
 * @property {Ref<Array>} tree - Reactive reference for the tree structure
 * @property {Ref<string>} selectionText - Reactive reference for the formatted selection text
 * @property {Ref<Array>} selectedOptions - Reactive reference for the selected options
 */
const {
  handleClear,
  handleUpdateFilters,
  handleUpdateIncludeAssets,
  searchTerm,
  tree,
  selectionText,
  selectedOptions,
} = useSpacesAndAssetsTree(props.buildings, props.type, props.modelValue, emit);

/**
 * Watch effect to remove the filter when the combobox is closed and there are selected options
 */
watchEffect(() => {
  if (!props.open && isEmpty(selectedOptions(props.modelValue))) {
    emit("removeFilter");
  }
});
</script>
